import { ReactNode, useCallback, useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"

import { APIError, useAuth } from "../../../../app/modules/auth"
import { formatLeadItemDate } from "../../../../_metronic/partials/widgets/debugging/DebugPipedrive"
import {
    PassareAcquaintance,
    PassareCase,
    PassareCaseDetail as PassareCaseDetailData,
    getPassareCaseByUniqueIdentifier,
    refreshPassareCaseByUniqueIdentifier,
} from "../../../core/_requests"
import MogulHeader from "../../../components/MogulHeader"

//
// Helpers
//

function getDecedentName(c: PassareCase): string {
    const cr = c.public_data.case_result
    if (c.decedent_data?.display_name) {
        return c.decedent_data.display_name
    }
    if (cr?.decedent_person?.person) {
        const p = cr.decedent_person.person
        return [p.title, p.first_name, p.middle_name, p.last_name, p.suffix].filter(Boolean).join(" ")
    }
    if (cr?.decedent_animal) {
        return `${cr.decedent_animal.name}${cr.decedent_animal.breed ? ` (${cr.decedent_animal.breed})` : ""}`
    }
    return "Unknown Decedent"
}

function acquaintanceName(a: PassareAcquaintance): string {
    const r = a.public_data.acquaintance_result
    if (r?.display_name) {
        return r.display_name
    }
    if (r) {
        return [r.first_name, r.last_name].filter(Boolean).join(" ") || "Unknown"
    }
    return "Unknown"
}

function formatPlainDate(value?: string | null): string | null {
    if (!value) {
        return null
    }
    const datePart = value.split("T")[0]
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!match) {
        return value
    }
    const [, year, month, day] = match
    const dt = new Date(Number(year), Number(month) - 1, Number(day))
    if (isNaN(dt.getTime())) {
        return value
    }
    return dt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
}

function ageAtDeath(birth?: string | null, death?: string | null): number | null {
    if (!birth || !death) {
        return null
    }
    const b = new Date(birth.split("T")[0])
    const d = new Date(death.split("T")[0])
    if (isNaN(b.getTime()) || isNaN(d.getTime())) {
        return null
    }
    let age = d.getFullYear() - b.getFullYear()
    const monthDiff = d.getMonth() - b.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && d.getDate() < b.getDate())) {
        age--
    }
    if (age < 0 || age > 130) {
        return null
    }
    return age
}

function maskSsn(ssn?: string | null): string | null {
    if (!ssn) {
        return null
    }
    const digits = ssn.replace(/\D/g, "")
    if (digits.length < 4) {
        return "•••"
    }
    return `•••-••-${digits.slice(-4)}`
}

function statusBadgeClass(status?: string | null): string {
    switch ((status || "").toLowerCase()) {
        case "closed":
            return "badge-light-success"
        case "new":
            return "badge-light-primary"
        case "in progress":
            return "badge-light-warning"
        default:
            return "badge-light"
    }
}

function acquaintanceAddress(a: PassareAcquaintance): string | null {
    const ad = a.public_data.address
    if (!ad) {
        return null
    }
    const street = [ad.address1, ad.address2, ad.address3, ad.address4].filter(Boolean).join(", ")
    const cityState = [ad.city, ad.state].filter(Boolean).join(", ")
    const full = [street, cityState, ad.zip].filter(Boolean).join(" ")
    return full || null
}

function ghlContactLink(a: PassareAcquaintance): string | null {
    const ghl = a.private_data.ghl_contact
    if (!ghl?.ghl_contact_id) {
        return null
    }
    return `https://app.premier360platform.com/v2/location/${ghl.ghl_location_id}/contacts/detail/${ghl.ghl_contact_id}`
}

// Only let web URLs into an href. Passare-sourced values (case_url,
// pc_payment_link) are external data, so a javascript:/data: scheme here would
// be stored XSS — reject anything that isn't http(s).
function safeHref(url?: string | null): string | undefined {
    if (!url) {
        return undefined
    }
    const trimmed = url.trim()
    return /^https?:\/\//i.test(trimmed) ? trimmed : undefined
}

//
// Presentational building blocks
//

function Field({ label, children, col = "col-md-6" }: { label: string; children?: ReactNode; col?: string }) {
    const empty = children === null || children === undefined || children === ""
    return (
        <div className={col}>
            <div className="text-muted fs-8 fw-semibold text-uppercase mb-1">{label}</div>
            <div className="fs-6 fw-bold text-gray-800">
                {empty ? <span className="text-muted fw-normal">—</span> : children}
            </div>
        </div>
    )
}

function SectionCard({
    title,
    children,
    headerRight,
}: {
    title: string
    children: ReactNode
    headerRight?: ReactNode
}) {
    return (
        <div className="card mb-5 shadow-sm">
            <div className="card-header align-items-center">
                <h3 className="card-title fw-bold text-gray-800">{title}</h3>
                {headerRight && <div className="card-toolbar">{headerRight}</div>}
            </div>
            <div className="card-body">{children}</div>
        </div>
    )
}

//
// Main component
//

function PassareCaseDetail() {
    const { passare_case_unique_identifier: caseId } = useParams<{
        passare_case_unique_identifier: string
    }>()

    const { currentUser } = useAuth()
    const navigate = useNavigate()

    const [detail, setDetail] = useState<PassareCaseDetailData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [refreshing, setRefreshing] = useState(false)
    const [refreshMessage, setRefreshMessage] = useState<{ type: "success" | "error"; text: string } | null>(null)

    const [showRaw, setShowRaw] = useState(false)

    const load = useCallback(async () => {
        if (!caseId) {
            setError("Missing case identifier")
            setLoading(false)
            return
        }
        setLoading(true)
        setError(null)
        try {
            const result = await getPassareCaseByUniqueIdentifier(caseId)
            setDetail(result)
        } catch (exception) {
            console.error(exception)
            setError(
                exception instanceof APIError
                    ? exception.message
                    : exception instanceof Error
                        ? exception.message
                        : "Failed to load case"
            )
        } finally {
            setLoading(false)
        }
    }, [caseId])

    const refreshFromPassare = useCallback(async () => {
        if (!caseId) {
            return
        }
        setRefreshing(true)
        setRefreshMessage(null)
        try {
            const result = await refreshPassareCaseByUniqueIdentifier(caseId)
            setDetail(result)
            setRefreshMessage({ type: "success", text: "Pulled the latest case data from Passare." })
        } catch (exception) {
            console.error(exception)
            setRefreshMessage({
                type: "error",
                text:
                    exception instanceof APIError
                        ? exception.message
                        : exception instanceof Error
                            ? exception.message
                            : "Failed to refresh from Passare",
            })
        } finally {
            setRefreshing(false)
        }
    }, [caseId])

    useEffect(() => {
        load()
    }, [load])

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center py-20">
                <span className="spinner-border text-primary" role="status" />
                <span className="ms-3 text-muted">Loading case…</span>
            </div>
        )
    }

    if (error || !detail) {
        return (
            <div className="py-10">
                <div className="alert alert-danger" role="alert">
                    {error || "Failed to load case"}
                </div>
                <div className="d-flex gap-3">
                    <button className="btn btn-primary" onClick={load}>
                        Retry
                    </button>
                    <button className="btn btn-light" onClick={() => navigate(-1)}>
                        Go back
                    </button>
                </div>
            </div>
        )
    }

    const { passareCase, passareOrganization, acquaintances } = detail
    const caseResult = passareCase.public_data.case_result
    const person = caseResult?.decedent_person?.person
    const animal = caseResult?.decedent_animal
    const decedentData = passareCase.decedent_data
    const disposition = passareCase.disposition_data

    const birthDate = person?.birth_date
    const deathDate = decedentData?.death_date
    const age = ageAtDeath(birthDate, deathDate)
    const hasHydrated = Boolean(caseResult)

    const residence = caseResult?.decedent_person?.address
    const residenceText = residence
        ? [residence.city, residence.state, residence.county].filter(Boolean).join(", ")
        : null

    const caseUrl = safeHref(caseResult?.case_url)
    const paymentLink = safeHref(caseResult?.pc_payment_link)

    const hasDisposition =
        Boolean(disposition) &&
        Boolean(
            disposition?.disposition_type ||
            disposition?.disposition_date ||
            disposition?.cemetery?.name ||
            disposition?.crematory ||
            disposition?.permit_number
        )

    const plot = disposition
        ? [
            disposition.section ? `Section ${disposition.section}` : null,
            disposition.block ? `Block ${disposition.block}` : null,
            disposition.lot ? `Lot ${disposition.lot}` : null,
            disposition.grave ? `Grave ${disposition.grave}` : null,
        ]
            .filter(Boolean)
            .join(" · ")
        : ""

    const dispositionAddressText = disposition?.disposition_address
        ? [
            disposition.disposition_address.address1,
            disposition.disposition_address.city,
            disposition.disposition_address.state,
            disposition.disposition_address.zip,
        ]
            .filter(Boolean)
            .join(", ")
        : null

    return (
        <div className="position-relative pb-10">
            <MogulHeader title={getDecedentName(passareCase)} />

            {/* Sub-header: badges + actions */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-5">
                <div className="d-flex flex-wrap align-items-center gap-2">
                    {caseResult?.case_status && (
                        <span className={`badge ${statusBadgeClass(caseResult.case_status)} fs-7`}>
                            {caseResult.case_status}
                        </span>
                    )}
                    {caseResult?.client_service_type_name && (
                        <span className="badge badge-light-info fs-7">{caseResult.client_service_type_name}</span>
                    )}
                    {animal && <span className="badge badge-light-warning fs-7">Pet</span>}
                    <span className="badge badge-light fs-8 text-muted">{passareCase.unique_identifier}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-light" onClick={() => navigate(-1)}>
                        ← Back
                    </button>
                    {caseUrl && (
                        <a
                            className="btn btn-sm btn-light-primary"
                            href={caseUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Open in Passare
                        </a>
                    )}
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={refreshFromPassare}
                        disabled={refreshing}
                    >
                        {refreshing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                                Refreshing…
                            </>
                        ) : (
                            "Refresh from Passare"
                        )}
                    </button>
                </div>
            </div>

            {refreshMessage && (
                <div
                    className={`alert ${refreshMessage.type === "success" ? "alert-success" : "alert-danger"} d-flex align-items-center`}
                    role="alert"
                >
                    <span className="flex-grow-1">{refreshMessage.text}</span>
                    <button
                        type="button"
                        className="btn btn-sm btn-icon btn-active-light-secondary"
                        onClick={() => setRefreshMessage(null)}
                        aria-label="Dismiss"
                    >
                        ✕
                    </button>
                </div>
            )}

            {!hasHydrated && (
                <div className="alert alert-warning" role="alert">
                    This case hasn’t been hydrated from Passare yet, so most details are blank. Try{" "}
                    <strong>Refresh from Passare</strong> to pull the latest data. (This can fail if the
                    organization’s Passare token lacks case-read permission.)
                </div>
            )}

            {/* Decedent */}
            <SectionCard title={animal ? "Decedent (Pet)" : "Decedent"}>
                {animal ? (
                    <div className="row g-5">
                        <Field label="Name">{animal.name}</Field>
                        <Field label="Type">{animal.type}</Field>
                        <Field label="Breed">{animal.breed}</Field>
                        <Field label="Gender">{animal.gender}</Field>
                        <Field label="Weight">{animal.weight}</Field>
                        <Field label="Date of Death">{formatPlainDate(animal.death) || formatPlainDate(deathDate)}</Field>
                    </div>
                ) : (
                    <div className="row g-5">
                        <Field label="Full Name">{getDecedentName(passareCase)}</Field>
                        <Field label="Gender">{person?.gender}</Field>
                        <Field label="Date of Birth">{formatPlainDate(birthDate)}</Field>
                        <Field label="Date of Death">{formatPlainDate(deathDate)}</Field>
                        <Field label="Age at Death">{age !== null ? `${age} years` : null}</Field>
                        <Field label="Marital Status">{decedentData?.marital_status}</Field>
                        <Field label="Occupation">{decedentData?.occupation}</Field>
                        <Field label="Employer">{decedentData?.employer}</Field>
                        <Field label="Residence">{residenceText}</Field>
                        <Field label="Veteran">
                            {decedentData?.veteran === true || caseResult?.decedent_person?.veteran === true
                                ? "Yes"
                                : decedentData?.veteran === false
                                    ? "No"
                                    : null}
                        </Field>
                        {currentUser?.is_admin && <Field label="SSN">{maskSsn(decedentData?.ssn)}</Field>}
                    </div>
                )}
            </SectionCard>

            {/* Case details */}
            <SectionCard title="Case Details">
                <div className="row g-5">
                    <Field label="Status">{caseResult?.case_status}</Field>
                    <Field label="Service Type">{caseResult?.client_service_type_name}</Field>
                    <Field label="Funeral Director">{caseResult?.case_assigned_to}</Field>
                    <Field label="Case Types">{caseResult?.case_types?.length ? caseResult.case_types.join(", ") : null}</Field>
                    <Field label="Branch">{passareCase.branch_name}</Field>
                    <Field label="Organization">
                        {passareOrganization ? (
                            <Link to={`/passare/organizations/${passareOrganization.unique_identifier}`}>
                                {passareOrganization.organization_name || "View organization"}
                            </Link>
                        ) : null}
                    </Field>
                    <Field label="Case Identifier">{caseResult?.case_identifier}</Field>
                    <Field label="Signing Date">
                        {passareCase.case_signed_at ? formatLeadItemDate(passareCase.case_signed_at) : null}
                    </Field>
                    <Field label="First Seen">{passareCase.inserted_at ? formatLeadItemDate(passareCase.inserted_at) : null}</Field>
                    <Field label="Last Updated">{passareCase.updated_at ? formatLeadItemDate(passareCase.updated_at) : null}</Field>
                    {paymentLink && (
                        <Field label="Payment Link">
                            <a href={paymentLink} target="_blank" rel="noreferrer">
                                Open payment link
                            </a>
                        </Field>
                    )}
                </div>
            </SectionCard>

            {/* Disposition */}
            {hasDisposition && (
                <SectionCard title="Disposition">
                    <div className="row g-5">
                        <Field label="Type">{disposition?.disposition_type}</Field>
                        <Field label="Date">{formatPlainDate(disposition?.disposition_date)}</Field>
                        <Field label="Time">{disposition?.disposition_time}</Field>
                        <Field label="Permit Number">{disposition?.permit_number}</Field>
                        <Field label="Cemetery">{disposition?.cemetery?.name}</Field>
                        <Field label="Crematory">
                            {disposition?.crematory?.name || (disposition?.crematory ? "Yes" : null)}
                        </Field>
                        <Field label="Plot" col="col-md-6">{plot || null}</Field>
                        <Field label="Disposition Address" col="col-md-6">{dispositionAddressText}</Field>
                    </div>
                </SectionCard>
            )}

            {/* Family / Leads */}
            <SectionCard
                title="Family & Leads"
                headerRight={
                    <span className="badge badge-light-primary">
                        {acquaintances.length} {acquaintances.length === 1 ? "person" : "people"}
                    </span>
                }
            >
                {acquaintances.length === 0 ? (
                    <div className="text-muted">No related people have been recorded for this case yet.</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-row-bordered align-middle gy-3">
                            <thead>
                                <tr className="fw-bold text-muted fs-7 text-uppercase">
                                    <th>Name</th>
                                    <th>Relationship</th>
                                    <th>Contact</th>
                                    <th>Address</th>
                                    <th>FRM Sync</th>
                                </tr>
                            </thead>
                            <tbody>
                                {acquaintances.map((a) => {
                                    const result = a.public_data.acquaintance_result
                                    const phones = a.public_data.phone_numbers
                                    const ghlLink = safeHref(ghlContactLink(a))
                                    const address = acquaintanceAddress(a)
                                    return (
                                        <tr key={a.unique_identifier}>
                                            <td>
                                                <span className="fw-bold text-gray-800">{acquaintanceName(a)}</span>
                                                {result?.is_deceased && (
                                                    <span className="badge badge-light-dark ms-2 fs-8">Deceased</span>
                                                )}
                                            </td>
                                            <td>{result?.relationship || <span className="text-muted">—</span>}</td>
                                            <td>
                                                {phones?.home_phone && <div className="fs-7">Home: {phones.home_phone}</div>}
                                                {phones?.mobile_phone && <div className="fs-7">Mobile: {phones.mobile_phone}</div>}
                                                {phones?.work_phone && <div className="fs-7">Work: {phones.work_phone}</div>}
                                                {a.public_data.email_address && (
                                                    <div className="fs-7">{a.public_data.email_address}</div>
                                                )}
                                                {!phones?.home_phone &&
                                                    !phones?.mobile_phone &&
                                                    !phones?.work_phone &&
                                                    !a.public_data.email_address && <span className="text-muted">—</span>}
                                            </td>
                                            <td>{address || <span className="text-muted">—</span>}</td>
                                            <td>
                                                {ghlLink ? (
                                                    <a href={ghlLink} target="_blank" rel="noreferrer">
                                                        <span className="badge badge-light-success">Synced — View in FRM</span>
                                                    </a>
                                                ) : (
                                                    <span className="badge badge-light text-muted">Not synced</span>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>

            {/* Raw data (admins) */}
            {currentUser?.is_admin && (
                <SectionCard
                    title="Raw Data (admin)"
                    headerRight={
                        <button className="btn btn-sm btn-light" onClick={() => setShowRaw((value) => !value)}>
                            {showRaw ? "Hide" : "Show"}
                        </button>
                    }
                >
                    {showRaw ? (
                        <pre className="bg-light p-4 rounded text-gray-700 mb-0" style={{ maxHeight: 500, overflow: "auto" }}>
                            {JSON.stringify(
                                {
                                    public_data: passareCase.public_data,
                                    disposition_data: passareCase.disposition_data,
                                    decedent_data: passareCase.decedent_data,
                                },
                                null,
                                2
                            )}
                        </pre>
                    ) : (
                        <div className="text-muted">Hidden. Click “Show” to inspect the raw Passare payload.</div>
                    )}
                </SectionCard>
            )}
        </div>
    )
}

export default PassareCaseDetail
